import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSearchParams, useParams, useNavigate} from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import FiltersLabel from '../../components/FiltersLabel';
import {fetchAuditLogActions, fetchAuditLogs, fetchAuditLogWithRelated, clearSelectedLog} from '../../slices/audit-logs.slice';
import Loading from '../../components/Loading';
import { DatePicker } from '../../components/DateTime';
import {
    Badge,
    Button,
    ButtonGroup,
    Label,
    ListGroup,
    ListGroupItem, Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import {getFiterDatesLabel} from '../../utils/dates';
import SearchBar from '../../components/SearchBar';
import {imgUrl} from '../../utils/api-request';
import {fetchOrganizations} from '../../slices/organizations.slice';
import {fetchUsers} from '../../slices/user.slice';
import AuditLogDetailPanel from './AuditLogDetailPanel';
import OrganizationPatch from '../../components/OrganizationPatch';
import Avatar from '../../components/Avatar';
import ExportModal from '../../components/ExportModal';
import api from '../../api';
import './AuditLogs.scss';

const AuditLogs = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { logId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { list: auditLogs, actions, status, pagination } = useSelector(state => state.auditLogs);
    const { list: users } = useSelector(state => state.users);
    const { list: organizations } = useSelector(state => state.organizations);
    const [searchValue, setSearchInput] = useState(searchParams.get('search') || '');
    const [searchModalValue, setSearchModalValue] = useState('');
    const debounceRef = useRef(null);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '', filterName: '' });
    const [searchActionsValue, setSearchActionsValue] = useState('');
    const [exportModal, setExportModal] = useState(false);

    // Panel selection based on URL
    const isPanelOpen = !!logId;

    const selectAuditLog = (auditLogId) => {
        navigate(`/audit/${auditLogId}${window.location.search}`);
    };

    const closePanel = () => {
        dispatch(clearSelectedLog());
        navigate(`/audit${window.location.search}`);
    };

    // Fetch audit log with related when logId changes
    useEffect(() => {
        if (logId) {
            dispatch(fetchAuditLogWithRelated(logId));
        }
    }, [dispatch, logId]);

    const parseURLFilters = () => {
        const actionsParam = searchParams.get('actions');
        const usersParam = searchParams.get('users');
        const organizationsParam = searchParams.get('organizations');
        const dateFromParam = searchParams.get('dateFrom');
        const dateToParam = searchParams.get('dateTo');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');
        const severityParam = searchParams.get('severity');
        const includeCronjobParam = searchParams.get('includeCronjob');

        return {
            actions: actionsParam ? actionsParam.split(',') : [],
            users: usersParam ? usersParam.split(',') : [],
            organizations: organizationsParam ? organizationsParam.split(',') : [],
            severity: severityParam ? severityParam.split(',') : [],
            includeCronjob: includeCronjobParam === 'true',
            createdAt: {
                from: dateFromParam ? new Date(dateFromParam) : null,
                to: dateToParam ? new Date(dateToParam) : null,
            },
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1,
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());

    const toggleFilterModal = (filter, filterName) => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter, filterName });
    };

    const formatDateForURL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.actions.length > 0) {
            params.set('actions', newFilters.actions.join(','));
        }
        if (newFilters.users.length > 0) {
            params.set('users', newFilters.users.join(','));
        }
        if (newFilters.organizations.length > 0) {
            params.set('organizations', newFilters.organizations.join(','));
        }
        if (newFilters.severity?.length > 0) {
            params.set('severity', newFilters.severity.join(','));
        }
        if (newFilters.includeCronjob) {
            params.set('includeCronjob', 'true');
        }
        if (newFilters.createdAt.from) {
            params.set('dateFrom', formatDateForURL(newFilters.createdAt.from));
        }
        if (newFilters.createdAt.to) {
            params.set('dateTo', formatDateForURL(newFilters.createdAt.to));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }

        setSearchParams(params);
    };

    const handleSearch = (event) => {
        event.preventDefault();
        const query = event.target.value;
        setSearchInput(query);

        // Debounce the server-side search
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setFilterBy(prev => {
                const newFilters = { ...prev, search: query, page: 1 };
                updateURLParams(newFilters);
                return newFilters;
            });
        }, 400);
    };

    const toggleFilter = (filterKey, value) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy[filterKey].includes(value);
            const newFilters = {
                ...prevFilterBy,
                [filterKey]: isSelected
                    ? prevFilterBy[filterKey].filter((item) => item !== value)
                    : [...prevFilterBy[filterKey], value],
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const filterTextUser = (user) => {
        return [user.firstName, user.lastName, user.username, user.email]
            .some(field => field?.toLowerCase().includes(searchModalValue.toLowerCase()));
    };

    const handleCreatedAt = (period) => (date) => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                createdAt: {
                    ...prevFilterBy.createdAt,
                    [period]: date,
                },
                page: 1,
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const areFiltersActive = useMemo(() => {
        const { actions, users, organizations, createdAt, severity, includeCronjob } = filterBy;

        return (
            actions.length > 0 ||
            users.length > 0 ||
            organizations.length > 0 ||
            severity.length > 0 ||
            includeCronjob ||
            createdAt.from !== null ||
            createdAt.to !== null
        );
    }, [filterBy]);

    const clearFilters = () => {
        const newFilters = {
            actions: [],
            users: [],
            organizations: [],
            severity: [],
            includeCronjob: false,
            createdAt: { from: null, to: null },
            search: '',
            page: 1,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = {
            ...filterBy,
            page: newPage,
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handleExport = async (fields, filetype = 'csv') => {
        try {
            const params = {
                ...filterBy,
                export: filetype,
                fields,
            };

            const response = await api.get('/audit-logs', {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit-logs.${filetype}`);
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        const params = {
            actions: filterBy.actions,
            users: filterBy.users,
            organizations: filterBy.organizations,
            page: filterBy.page,
        };

        // Full-text search
        if (filterBy.search) {
            params.search = filterBy.search;
        }

        // Severity filter
        if (filterBy.severity?.length > 0) {
            params.severity = filterBy.severity.join(',');
        }

        // Cronjob filter: by default exclude cronjobs (send cronjob=false)
        // Only include all when includeCronjob is true
        if (!filterBy.includeCronjob) {
            params.cronjob = 'false';
        }

        if (filterBy.createdAt.from) {
            const fromDate = new Date(filterBy.createdAt.from);
            fromDate.setHours(0, 0, 0, 0);
            params.createdAt = {
                ...params.createdAt,
                from: fromDate.toISOString(),
            };
        }

        if (filterBy.createdAt.to) {
            const toDate = new Date(filterBy.createdAt.to);
            toDate.setHours(23, 59, 59, 999);
            params.createdAt = {
                ...params.createdAt,
                to: toDate.toISOString(),
            };
        }

        dispatch(fetchAuditLogs(params));
    }, [dispatch, filterBy]);

    useEffect(() => {
        dispatch(fetchAuditLogActions());
        dispatch(fetchOrganizations())
        dispatch(fetchUsers())
    }, [dispatch]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="auditlog-view page px-4 py-3 d-flex flex-column overflow-y-auto">
            <PageHeader
                title="Audit Logs"
                searchBar={{searchValue, handleSearch}}
            />

            <div className="d-flex justify-content-start align-items-center gap-3">
                <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                <ButtonGroup className="gap-2">
                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('actions')}>
                        {filterBy.actions.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.actions.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-sort me-2"/>
                        )}
                        Actions
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('users')}>
                        {filterBy.users.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.users.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-users me-2"/>
                        )}
                        Users
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('organizations')}>
                        {filterBy.organizations.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.organizations.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-building me-2"/>
                        )}
                        Organizations
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('createdAt', 'dates')}>
                        {filterBy.createdAt.from || filterBy.createdAt.to ? (
                            <Badge color="success" className="me-2 text-white">
                                <span className="fas fa-calendar-check"/>
                            </Badge>
                        ) : (
                            <span className="fas fa-calendar-week me-2"/>
                        )}
                        {getFiterDatesLabel(filterBy.createdAt, 'Dates')}
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('severity')}>
                        {filterBy.severity.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.severity.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-exclamation-triangle me-2"/>
                        )}
                        Severity
                    </Button>

                    <Button
                        color={filterBy.includeCronjob ? 'success' : 'primary'}
                        outline={!filterBy.includeCronjob}
                        size="sm"
                        className="rounded-3"
                        onClick={() => {
                            const newFilters = {
                                ...filterBy,
                                includeCronjob: !filterBy.includeCronjob,
                                page: 1,
                            };
                            setFilterBy(newFilters);
                            updateURLParams(newFilters);
                        }}
                    >
                        <span className="fas fa-robot me-2"/>
                        Cronjobs
                    </Button>
                </ButtonGroup>

                <div className="flex-grow-1">
                    <span className="fas fa-list me-2 text-primary"/>
                    showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                </div>

                <div className="text-primary">
                    <Button color="link" onClick={() => setExportModal(true)}
                            className="text-decoration-none">
                        <span className="fas fa-file-excel me-1"/> Export
                    </Button>
                </div>
            </div>

            {status === 'loading' && <Loading length={3}/>}

            {status === 'success' && (
                <div className="audit-log-list">
                    {auditLogs.map((auditLog) => {
                        const severity = auditLog.severity || 'info';
                        const severityIcons = {
                            debug: 'fa-bug',
                            info: 'fa-info',
                            warning: 'fa-exclamation',
                            critical: 'fa-exclamation'
                        };

                        return (
                            <div key={auditLog._id}
                                 className={`audit-log-item my-2 p-3 bg-broken border-start d-flex justify-content-between severity-${severity} ${logId === auditLog._id ? 'selected' : ''}`}
                                 onClick={() => selectAuditLog(auditLog._id)}>
                                <div className="severity-icon">
                                    <span className={`fas ${severityIcons[severity]}`} />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span className="text-primary">
                                            [{auditLog.entity.toUpperCase()}]
                                        </span>
                                        <span className="text-white-50">
                                            {auditLog.action.toLowerCase().replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <p className="mb-0">
                                        {auditLog.cronjob && (
                                            <span className="badge bg-warning text-dark me-2">
                                                <span className="fas fa-robot fa-sm" /> Cronjob
                                            </span>
                                        )}
                                        {auditLog.details}
                                    </p>
                                </div>

                                <div className="d-flex flex-column align-items-end gap-2">
                                    <div className="small text-white-50">
                                        <span className="fas fa-calendar me-2"/>
                                        {new Date(auditLog.createdAt).toLocaleString()}
                                    </div>
                                    {auditLog.organization && (
                                        <OrganizationPatch organization={auditLog.organization} />
                                    )}
                                    {auditLog.user && (
                                        <div className="d-flex align-items-center gap-2 p-2 bg-black/30 rounded">
                                            <Avatar
                                                imageUrl={auditLog.user.profilePic?.imageUrl}
                                                name={`${auditLog.user.firstName} ${auditLog.user.lastName}`}
                                                size={28}
                                            />
                                            <span className="small">{auditLog.user.firstName} {auditLog.user.lastName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={pagination.currentPage === 1}
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                            >
                                <span className="fas fa-chevron-left me-2"/>
                                Previous
                            </Button>

                            <span className="text-white">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </span>

                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={!pagination.hasMore}
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                            >
                                Next
                                <span className="fas fa-chevron-right ms-2"/>
                            </Button>
                        </div>
                    )}

                    {/* Severity Legend */}
                    <div className="severity-legend">
                        <div className="legend-item debug">
                            <div className="legend-dot"><span className="fas fa-bug" /></div>
                            Debug
                        </div>
                        <div className="legend-item info">
                            <div className="legend-dot"><span className="fas fa-info" /></div>
                            Info
                        </div>
                        <div className="legend-item warning">
                            <div className="legend-dot"><span className="fas fa-exclamation" /></div>
                            Warning
                        </div>
                        <div className="legend-item critical">
                            <div className="legend-dot"><span className="fas fa-exclamation" /></div>
                            Critical
                        </div>
                    </div>
                </div>
            )}

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="lg" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filterName || filterModal.filter}
                </ModalHeader>
                <ModalBody>
                    {filterModal.filter === 'actions' && (
                        <div className="actions-filter-modal">
                            <SearchBar
                                onChange={({ target }) => setSearchActionsValue(target.value)}
                                initialValue={searchActionsValue}
                            />
                            <ListGroup>
                                {Object.keys(actions)
                                    .filter(action => action.toLowerCase().includes(searchActionsValue.toLowerCase()))
                                    .map(action => (
                                    <ListGroupItem
                                        key={action}
                                        onClick={() => toggleFilter('actions', action)}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.actions.includes(action)}
                                        className="border-0 border-start border-primary border-2 position-relative p-2
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <div className="flex-grow-1">
                                            {action.replace(/_/g, ' ')}
                                        </div>
                                        {filterBy.actions.includes(action) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'createdAt' && (
                        <div className="createdAt-filter-modal">
                            <Label>Date from</Label>
                            <DatePicker
                                value={filterBy.createdAt.from}
                                onChange={handleCreatedAt('from')}
                                placeholder="From"
                                clearable
                            />
                            <Label className="mt-4">Date to</Label>
                            <DatePicker
                                value={filterBy.createdAt.to}
                                onChange={handleCreatedAt('to')}
                                placeholder="To"
                                clearable
                            />
                        </div>
                    )}

                    {filterModal.filter === 'organizations' && (
                        <div className="organizations-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {organizations.map(({ _id, name, logo }) => name.toLowerCase().includes(searchModalValue.toLowerCase()) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('organizations', _id)}
                                        key={_id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.organizations.includes(_id)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <img src={imgUrl(logo?.imageUrl)} width={50} alt=""/>
                                        <div className="flex-grow-1">
                                            {name}
                                        </div>
                                        {filterBy.organizations.includes(_id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'users' && (
                        <div className="organizations-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {users.map(user => filterTextUser(user) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('users', user._id)}
                                        key={user._id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.users.includes(user._id)}
                                        className="border-0 border-start border-primary border-2 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <Avatar
                                            imageUrl={user.profilePic?.imageUrl}
                                            name={`${user.firstName} ${user.lastName}`}
                                            size={50}
                                        />
                                        <div className="flex-grow-1">
                                            <span className="text-primary me-3">
                                                {user.firstName} {user.lastName}
                                            </span>
                                            (<code className="text-white">{user.username}</code>)
                                            {user.parentOrganization && (
                                                <p className="small mb-0">
                                                    <span className="fas fa-building me-2"/>
                                                    {user.parentOrganization.name}
                                                </p>
                                            )}
                                        </div>
                                        {filterBy.users.includes(user._id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'severity' && (
                        <div className="severity-filter-modal">
                            <ListGroup>
                                {[
                                    { value: 'debug', label: 'Debug', icon: 'fa-bug', color: 'secondary' },
                                    { value: 'info', label: 'Info', icon: 'fa-info', color: 'info' },
                                    { value: 'warning', label: 'Warning', icon: 'fa-exclamation', color: 'warning' },
                                    { value: 'critical', label: 'Critical', icon: 'fa-exclamation', color: 'danger' }
                                ].map(({ value, label, icon, color }) => (
                                    <ListGroupItem
                                        key={value}
                                        onClick={() => toggleFilter('severity', value)}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.severity.includes(value)}
                                        className={`border-0 border-start border-${color} border-4 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2`}
                                    >
                                        <div className={`rounded-circle bg-dark d-flex align-items-center justify-content-center text-${color}`}
                                             style={{ width: '32px', height: '32px', border: '2px solid currentColor' }}>
                                            <span className={`fas ${icon} fa-sm`} />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="fw-medium">{label}</span>
                                        </div>
                                        {filterBy.severity.includes(value) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success"/>
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>

            {isPanelOpen && (
                <AuditLogDetailPanel
                    logId={logId}
                    onClose={closePanel}
                />
            )}

            <ExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(false)}
                onExport={handleExport}
                entityName="Audit Logs"
                fields={[
                    'action',
                    'entity',
                    'details',
                    'severity',
                    'cronjob',
                    'createdAt',
                    'ipAddress',
                    'userAgent',
                ]}
                subFields={{
                    user: ['firstName', 'lastName', 'username', 'email'],
                    organization: ['name', 'country'],
                }}
                defaultSelectedFields={[
                    'action',
                    'entity',
                    'details',
                    'severity',
                    'createdAt',
                    'user.username',
                    'organization.name',
                ]}
            />
        </div>
    );
};

export default AuditLogs;
